import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const CharityThemeContext = createContext(undefined);
export { CharityThemeContext };

function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

function rgbToHex(r, g, b) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

function lightenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.floor(rgb.r + (255 - rgb.r) * percent));
  const g = Math.min(255, Math.floor(rgb.g + (255 - rgb.g) * percent));
  const b = Math.min(255, Math.floor(rgb.b + (255 - rgb.b) * percent));

  return rgbToHex(r, g, b);
}

function darkenColor(hex, percent) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.floor(rgb.r * (1 - percent));
  const g = Math.floor(rgb.g * (1 - percent));
  const b = Math.floor(rgb.b * (1 - percent));

  return rgbToHex(r, g, b);
}

function getContrastColor(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#000000';

  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

const defaultTheme = {
  primary: '#3B82F6',
  secondary: '#1E40AF',
  accent: '#10B981',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  gradientStart: '#3B82F6',
  gradientEnd: '#1E40AF',
  logoUrl: 'https://cms.tlffs.ca/uploads/header_Logo_4f095fb08b.svg',
  logoUrlDark: null,
  faviconUrl: null,
  fontFamily: 'Inter, sans-serif',
  headingFontFamily: 'Inter, sans-serif',
  buttonStyle: 'rounded',
  customCSS: null,
  charityName: 'TLF Fundraising Solutions',
  charityId: 'default'
};

export function CharityThemeProvider({ children }) {
  const [theme, setTheme] = useState(defaultTheme);
  const [isLoaded, setIsLoaded] = useState(false);

  function applyThemeToDOM(themeConfig) {
    const root = document.documentElement;

    root.style.setProperty('--charity-primary', themeConfig.primary);
    root.style.setProperty('--charity-primary-light', lightenColor(themeConfig.primary, 0.2));
    root.style.setProperty('--charity-primary-lighter', lightenColor(themeConfig.primary, 0.4));
    root.style.setProperty('--charity-primary-lightest', lightenColor(themeConfig.primary, 0.6));
    root.style.setProperty('--charity-primary-dark', darkenColor(themeConfig.primary, 0.2));
    root.style.setProperty('--charity-primary-darker', darkenColor(themeConfig.primary, 0.4));
    root.style.setProperty('--charity-primary-darkest', darkenColor(themeConfig.primary, 0.6));
    root.style.setProperty('--charity-primary-contrast', getContrastColor(themeConfig.primary));

    root.style.setProperty('--charity-secondary', themeConfig.secondary);
    root.style.setProperty('--charity-secondary-light', lightenColor(themeConfig.secondary, 0.2));
    root.style.setProperty('--charity-secondary-lighter', lightenColor(themeConfig.secondary, 0.4));
    root.style.setProperty('--charity-secondary-lightest', lightenColor(themeConfig.secondary, 0.6));
    root.style.setProperty('--charity-secondary-dark', darkenColor(themeConfig.secondary, 0.2));
    root.style.setProperty('--charity-secondary-darker', darkenColor(themeConfig.secondary, 0.4));
    root.style.setProperty('--charity-secondary-darkest', darkenColor(themeConfig.secondary, 0.6));
    root.style.setProperty('--charity-secondary-contrast', getContrastColor(themeConfig.secondary));

    root.style.setProperty('--charity-accent', themeConfig.accent);
    root.style.setProperty('--charity-accent-light', lightenColor(themeConfig.accent, 0.2));
    root.style.setProperty('--charity-accent-lighter', lightenColor(themeConfig.accent, 0.4));
    root.style.setProperty('--charity-accent-lightest', lightenColor(themeConfig.accent, 0.6));
    root.style.setProperty('--charity-accent-dark', darkenColor(themeConfig.accent, 0.2));
    root.style.setProperty('--charity-accent-darker', darkenColor(themeConfig.accent, 0.4));
    root.style.setProperty('--charity-accent-darkest', darkenColor(themeConfig.accent, 0.6));
    root.style.setProperty('--charity-accent-contrast', getContrastColor(themeConfig.accent));

    root.style.setProperty('--charity-success', themeConfig.success);
    root.style.setProperty('--charity-warning', themeConfig.warning);
    root.style.setProperty('--charity-error', themeConfig.error);

    root.style.setProperty('--charity-gradient-start', themeConfig.gradientStart);
    root.style.setProperty('--charity-gradient-end', themeConfig.gradientEnd);

    root.style.setProperty('--charity-font-family', themeConfig.fontFamily);
    root.style.setProperty('--charity-heading-font-family', themeConfig.headingFontFamily);

    if (themeConfig.customCSS) {
      const styleId = 'charity-custom-css';
      let styleElement = document.getElementById(styleId);

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }

      styleElement.textContent = themeConfig.customCSS;
    }
  };

  // Apply theme to DOM immediately during first render (not in useEffect)
  // to prevent FOUC — CSS variables must exist before the first paint.
  const initializedRef = useRef(false);
  if (!initializedRef.current) {
    initializedRef.current = true;
    if (typeof window !== 'undefined' && window.CHARITY_THEME) {
      const charityTheme = {
        primary: window.CHARITY_THEME.primary_color || defaultTheme.primary,
        secondary: window.CHARITY_THEME.secondary_color || defaultTheme.secondary,
        accent: window.CHARITY_THEME.accent_color || defaultTheme.accent,
        success: window.CHARITY_THEME.success_color || defaultTheme.success,
        warning: window.CHARITY_THEME.warning_color || defaultTheme.warning,
        error: window.CHARITY_THEME.error_color || defaultTheme.error,
        gradientStart: window.CHARITY_THEME.gradient_start || window.CHARITY_THEME.primary_color || defaultTheme.gradientStart,
        gradientEnd: window.CHARITY_THEME.gradient_end || window.CHARITY_THEME.secondary_color || defaultTheme.gradientEnd,
        logoUrl: window.CHARITY_THEME.logo_url || defaultTheme.logoUrl,
        logoUrlDark: window.CHARITY_THEME.logo_url_dark || null,
        faviconUrl: window.CHARITY_THEME.favicon_url || null,
        fontFamily: window.CHARITY_THEME.font_family || defaultTheme.fontFamily,
        headingFontFamily: window.CHARITY_THEME.heading_font_family || defaultTheme.headingFontFamily,
        buttonStyle: window.CHARITY_THEME.button_style || defaultTheme.buttonStyle,
        customCSS: window.CHARITY_THEME.custom_css || null,
        charityName: window.CHARITY_THEME.charity_name || defaultTheme.charityName,
        charityId: window.CHARITY_THEME.charity_id || defaultTheme.charityId
      };
      setTheme(charityTheme);
      applyThemeToDOM(charityTheme);
    } else {
      applyThemeToDOM(defaultTheme);
    }
  }

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const getColorVariations = (baseColor) => {
    return {
      base: baseColor,
      light: lightenColor(baseColor, 0.2),
      lighter: lightenColor(baseColor, 0.4),
      lightest: lightenColor(baseColor, 0.6),
      dark: darkenColor(baseColor, 0.2),
      darker: darkenColor(baseColor, 0.4),
      darkest: darkenColor(baseColor, 0.6),
      contrast: getContrastColor(baseColor)
    };
  };

  const value = {
    theme,
    isLoaded,
    colors: {
      primary: getColorVariations(theme.primary),
      secondary: getColorVariations(theme.secondary),
      accent: getColorVariations(theme.accent),
      success: getColorVariations(theme.success),
      warning: getColorVariations(theme.warning),
      error: getColorVariations(theme.error)
    },
    gradient: {
      start: theme.gradientStart,
      end: theme.gradientEnd,
      css: `linear-gradient(to right, ${theme.gradientStart}, ${theme.gradientEnd})`
    },
    branding: {
      logoUrl: theme.logoUrl,
      logoUrlDark: theme.logoUrlDark,
      faviconUrl: theme.faviconUrl,
      charityName: theme.charityName,
      charityId: theme.charityId
    },
    typography: {
      fontFamily: theme.fontFamily,
      headingFontFamily: theme.headingFontFamily
    },
    buttonStyle: theme.buttonStyle
  };

  return (
    <CharityThemeContext.Provider value={value}>
      {children}
    </CharityThemeContext.Provider>
  );
}

export function useCharityTheme() {
  const context = useContext(CharityThemeContext);
  if (context === undefined) {
    throw new Error('useCharityTheme must be used within a CharityThemeProvider');
  }
  return context;
}
